import axios from 'axios';
import ThemeButton from 'components/AntdFormInputs/theme-button';
import { parseHtml } from 'helpers';
import React, { useEffect, useState } from 'react';
import { Check, Copy, X } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAiContext,
  getContent,
  getOpenModal,
  setContent,
  setEditorValue,
  setOpenModal,
} from 'store/ai-modal';
import TypingEffect from './typing-effect';
import { TbPlaylistAdd } from 'react-icons/tb';
import { useClipboard } from 'hooks/use-clipboard';

const AiModalPopup = () => {
  const isOpenModal = useSelector(getOpenModal);
  const { clipboardCopy, copied } = useClipboard();
  const aiContext = useSelector(getAiContext);
  const isContent = useSelector(getContent);
  const dispatch = useDispatch();

  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(false);

  const sanitizeAiOutput = (text) => {
    if (!text) return '';
    let out = String(text).trim();

    // Remove common wrapper tokens some models return
    // e.g. "<s> ... </s>" or "<s> ... [/s]"
    out = out.replace(/^\s*<s>\s*/i, '');
    out = out.replace(/\s*<\/s>\s*$/i, '');
    out = out.replace(/\s*\[\/s\]\s*$/i, '');
    out = out.replace(/^\s*\[s\]\s*/i, '');
    // e.g. "[OUT] ... [/OUT]" (some LLMs do this even when told not to)
    out = out.replace(/^\s*\[out\]\s*/i, '');
    out = out.replace(/\s*\[\/out\]\s*$/i, '');
    // Generic bracketed wrappers: "[SOMETHING] ... [/SOMETHING]"
    // Only strip if it looks like a simple wrapper around the whole output
    out = out.replace(/^\s*\[[a-z_ -]{2,20}\]\s*/i, '');
    out = out.replace(/\s*\[\/[a-z_ -]{2,20}\]\s*$/i, '');

    // Remove surrounding quotes if the model returns: "...."
    if (
      (out.startsWith('"') && out.endsWith('"')) ||
      (out.startsWith('“') && out.endsWith('”'))
    ) {
      out = out.slice(1, -1).trim();
    }

    return out;
  };

  const getPlainTextFromHTML = (html) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };
  const checkingContent = getPlainTextFromHTML(isContent)?.length;

  const toneOptions = {
    general: 'General',
    formal: 'Formal',
    funny: 'Funny',
    conversational: 'Conversational',
    professional: 'Professional',
    persuasive: 'Persuasive',
    motivational: 'Motivational',
    sarcastic: 'Sarcastic',
    poetic: 'Poetic',
    simple: 'Simple',
    technical: 'Technical',
    storytelling: 'Storytelling',
    excited: 'Excited',
  };

  const buildAiPrompts = ({ tone, text }) => {
    const cleanText = String(text || '').trim();
    const wordCount = cleanText
      ? cleanText.split(/\s+/).filter(Boolean).length
      : 0;

    const baseConstraints = [
      'Return ONLY the final rewritten text.',
      'Do NOT wrap the output in <s>, </s>, [s], [/s], [OUT], [/OUT], quotes, backticks, or code blocks.',
      'Do NOT include markdown, HTML tags, bullets, numbering, or extra headings.',
      'Do NOT add explanations, commentary, or alternative versions.',
      'Preserve the original meaning and facts. Do not invent details.',
      'Keep the same point of view (I/you/we) and tense unless required for grammar.',
      `Keep length roughly similar (within ~10-20%). Input is about ${wordCount} words.`,
    ].join(' ');

    const toneInstructionMap = {
      general:
        'Correct grammar, punctuation, and sentence structure without changing meaning.',
      formal: 'Rewrite in a formal tone.',
      funny: 'Rewrite in a humorous tone (still clear and appropriate).',
      conversational: 'Rewrite in a casual, conversational tone.',
      professional:
        'Rewrite in a professional tone: clear, concise, neutral, and well-structured.',
      persuasive: 'Rewrite to be more persuasive (without exaggeration).',
      empathetic: 'Rewrite with a warm, understanding tone.',
      motivational: 'Rewrite in an inspiring, motivational tone.',
      sarcastic: 'Rewrite in a sarcastic tone (keep it readable).',
      poetic: 'Rewrite with poetic expression (keep meaning intact).',
      simple: 'Simplify the text using plain language.',
      technical: 'Rewrite in a technical tone (precise wording).',
      storytelling: 'Rewrite as a short story (same message).',
      excited: 'Rewrite with enthusiasm and energy.',
    };

    const toneInstruction =
      toneInstructionMap[tone] || toneInstructionMap.general;

    const system = `You are an expert editor. ${baseConstraints}`;
    const user = `${toneInstruction}\n\nText:\n${cleanText}`;

    return { system, user };
  };
  const handleClearTexts = () => {
    setSuggestion(null);
    dispatch(setContent(null));
    dispatch(setOpenModal(false));
  };

  useEffect(() => {
    if (isOpenModal && checkingContent) {
      handlePromptSubmit('general');
    }
  }, [isOpenModal]);

  const handlePromptSubmit = async (tone) => {
    const text = getPlainTextFromHTML(isContent);
    if (text.length > 10) {
      setLoading(true);
      try {
        const { system, user } = buildAiPrompts({ tone, text });
        const response = await axios.post(
          'https://openrouter.ai/api/v1/chat/completions',
          {
            model: 'mistralai/mistral-7b-instruct:free',
            messages: [
              {
                role: 'system',
                content: system,
              },
              {
                role: 'user',
                content: user,
              },
            ],
            max_tokens: 300,
          },
          {
            headers: {
              Authorization: `Bearer ${process.env.NEXT_PUBLIC_OPENROUTER_API_KEY}`,
              'Content-Type': 'application/json',
            },
          },
        );

        const result =
          response.data.choices?.[0]?.message?.content?.trim() ||
          'No suggestion received.';
        setSuggestion(sanitizeAiOutput(result));
      } catch (error) {
        console.error(
          'Error fetching suggestion:',
          error.response?.data || error.message,
        );
        setSuggestion('❌ Error fetching suggestion');
      } finally {
        setLoading(false);
      }
    } else {
      setSuggestion('');
    }
  };

  const handleCopy = () => {
    if (suggestion) {
      clipboardCopy(sanitizeAiOutput(suggestion));
    }
  };

  const handleInsert = () => {
    if (suggestion && aiContext?.full && aiContext?.selectedHtml) {
      const replacedHtml = aiContext.full.replace(
        aiContext.selectedHtml,
        sanitizeAiOutput(suggestion),
      );
      dispatch(setEditorValue(replacedHtml));
      dispatch(setOpenModal(false));
    } else if (suggestion) {
      dispatch(setEditorValue(sanitizeAiOutput(suggestion)));
      dispatch(setOpenModal(false));
    }
  };
  return (
    <>
      {isOpenModal ? (
        <div className="ai-popup-wrapper">
          <div className="ai-gradient-bg" />
          <div className="ai-modal">
            <div className="ai-header-modal">
              <h2>ChampsLms AI</h2>
              <ThemeButton
                padding="4px 4px"
                onClick={handleClearTexts}
                variant="cancel"
              >
                <X />
              </ThemeButton>
            </div>
            <hr />
            <section className="ai-text-editor-placeholder">
              {checkingContent ? (
                <div className="texteditor-replytext">
                  {parseHtml(isContent)}
                </div>
              ) : (
                'No content found. Please enter some text...'
              )}
              {suggestion ? <hr /> : null}
              {loading ? (
                ''
              ) : suggestion ? (
                <>
                  <div className="custom-bot-reply">
                    <svg
                      class="custom-ai-assistan-icon"
                      fill="#FA5252"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="24px"
                      height="24px"
                    >
                      <path d="M12,0C11.743,6.518,6.518,11.743,0,12c6.518,0.257,11.743,5.482,12,12c0.257-6.518,5.482-11.743,12-12	C17.482,11.743,12.257,6.518,12,0z M12,17.163C10.743,15.039,8.961,13.257,6.837,12C8.961,10.743,10.743,8.961,12,6.837	c1.257,2.124,3.039,3.906,5.163,5.163C15.039,13.257,13.257,15.039,12,17.163z" />
                    </svg>
                    <h6 className="mb-0">AI Assistant</h6>
                  </div>
                  <div className="ai-model-replytext">
                    <TypingEffect text={suggestion} />
                    <div className="copy-insert-button mt-2">
                      <button
                        className="custom-copy-insert"
                        title={copied ? 'Copied' : 'Copy'}
                        onClick={handleCopy}
                      >
                        {copied ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                      <button
                        className="custom-copy-insert"
                        title="Insert to Editor"
                        onClick={handleInsert}
                      >
                        <TbPlaylistAdd size={18} />
                      </button>
                    </div>
                  </div>
                </>
              ) : null}
            </section>
            <hr />
            {loading ? (
              <div className="custom-thinking-loader" disabled={loading}>
                <img src={'/images/rippleloading.svg'} height={60} />
              </div>
            ) : (
              <div className="ai-actions">
                {Object.entries(toneOptions).map(([tone, label]) => (
                  <button
                    key={tone}
                    className="ai-cancel"
                    onClick={() => handlePromptSubmit(tone)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            <p className="ai-hint">Try: General, Friendly, Change tone, etc.</p>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default AiModalPopup;
